import { useSelector } from 'react-redux'
import moment from 'moment'
import { parse } from 'query-string'

import { selectCurrentUser } from '../../../state/selectors'
import { User } from '../../../hooks/use-user'

import { STORAGE_KEY_PREFIX } from './use-onboarding'

interface OnboardingState {
  isEnabled: boolean
  isLoading: boolean
}

const NEW_USER_DAYS_THRESHOLD = 30
const CHECKOUT_PATHS = ['/checkout', '/pricing']

/**
 * Hook to manage the onboarding questionnaire notification state
 *
 * Rules:
 * - Only shown to new free users and trial users
 * - If account is older than 30 days, don't show anything
 * - If user completed onboarding, don't show anything
 * - If user has onboarding_survey_submitted flag, don't show anything
 */
export function useShouldShowOnboarding(): OnboardingState {
  const user: User = useSelector(selectCurrentUser())

  const isCheckoutPath = CHECKOUT_PATHS.some((path) =>
    window.location.pathname.toLowerCase().includes(path),
  )
  const isCheckoutInProgress = parse(window.location.search).checkout === 'true'
  const shouldBlockForCheckout = isCheckoutPath || isCheckoutInProgress

  if (!user || !user.created_at || !moment.unix(user.created_at).isValid()) {
    return {
      isEnabled: false,
      isLoading: true,
    }
  }

  if (user.flags?.onboarding_survey_submitted) {
    return {
      isEnabled: false,
      isLoading: false,
    }
  }

  // Don't show for existing paid users
  if (
    user.subscription &&
    (user.subscription.subscriptionStatus === 'active' ||
      user.subscription.subscriptionStatus === 'paid' ||
      user.subscription.subscriptionStatus === 'canceled' ||
      user.subscription.subscriptionStatus === 'past_due')
  ) {
    return {
      isEnabled: false,
      isLoading: false,
    }
  }

  const daysSinceCreation = moment().diff(moment.unix(user.created_at), 'days')

  // Don't show after 30 days
  if (daysSinceCreation >= NEW_USER_DAYS_THRESHOLD) {
    return {
      isEnabled: false,
      isLoading: false,
    }
  }

  // Don't show during checkout
  if (shouldBlockForCheckout) {
    return {
      isEnabled: false,
      isLoading: false,
    }
  }

  if (user.subscription?.subscriptionStatus === 'trialing') {
    const storageKey = `${STORAGE_KEY_PREFIX}_${user.id}_trial_session_count`
    const rawSessionCount = localStorage.getItem(storageKey)
    const sessionCount = Number(rawSessionCount || '0')

    if (!rawSessionCount || isNaN(sessionCount)) {
      return {
        isEnabled: false,
        isLoading: false,
      }
    }

    // Only show after first session
    if (sessionCount < 1) {
      return {
        isEnabled: false,
        isLoading: false,
      }
    }
  }

  return {
    isEnabled: true,
    isLoading: false,
  }
}
